// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import { Carousel } from '@realadvisor/carousel';
import { Image } from '@realadvisor/image';
import { Center } from '../../controls/Center';
import { Gallery } from '../../controls/gallery';
import { NoPhoto } from '../../controls/photo-helpers';
import { DevelopmentHeader } from './DevelopmentHeader';
import { DevelopmentProjectConsortium } from './DevelopmentProjectConsortium';
import { DevelopmentLocation } from './DevelopmentLocation';
import { DevelopmentActionCard } from './DevelopmentActionCard';
import { DevelopmentActionBar } from './DevelopmentActionBar';
import { DevelopmentLots } from './DevelopmentLots';
import { DevelopmentDescription } from './DevelopmentDescription';
import type { Development_development$key } from './__generated__/Development_development.graphql';

type Props = {|
  development: Development_development$key,
|};

export const Development = (props: Props): React.Node => {
  const { media } = useSystem();
  const [galleryVisibleAt, setGalleryVisibleAt] = React.useState(null);

  const ratio = 0.66;

  const development = useFragment(
    graphql`
      fragment Development_development on DevelopmentProduct {
        ...DevelopmentHeader_development
        ...DevelopmentProjectConsortium_development
        ...DevelopmentDescription_development
        ...DevelopmentLocation_development
        ...DevelopmentActionCard_development
        ...DevelopmentActionBar_development
        ...DevelopmentLots_development
        developmentImages {
          title
          displayOnListing
          crop
          backgroundColor
          image {
            url
          }
        }
      }
    `,
    props.development,
  );

  const images = (development.developmentImages || [])
    .filter(x => x.displayOnListing)
    .map(({ title, image, crop, backgroundColor }) => {
      const [bucketName, fileName] = image.url.split('/').slice(3, 5);
      return { bucketName, fileName, title, crop, backgroundColor };
    });

  return (
    <>
      <Carousel
        media={[
          { gap: 1, itemsOnScreen: 1, aspectRatio: 0.8 },
          { gap: 6, itemsOnScreen: 2.3, aspectRatio: 0.66 },
          { gap: 12, itemsOnScreen: 2.3, aspectRatio: 0.66 },
        ]}
        fillEmpty={true}
      >
        {images.length === 0 ? (
          <NoPhoto />
        ) : (
          images.map((item, index) => {
            return (
              <Box
                key={index}
                css={{ cursor: 'pointer', width: '100%', height: '100%' }}
                onClick={() => setGalleryVisibleAt(index)}
              >
                <Image
                  src={{
                    bucketName: item.bucketName,
                    fileName: item.fileName,
                  }}
                  srcSize={[
                    [375, 375 * ratio],
                    [600, 600 * ratio],
                    [900, 900 * ratio],
                  ]}
                  options={{
                    c: item.crop,
                    b: item.backgroundColor,
                  }}
                  objectFit={item.crop === 'fit' ? 'contain' : 'cover'}
                />
              </Box>
            );
          })
        )}
      </Carousel>

      {galleryVisibleAt != null && (
        <Gallery
          startAt={galleryVisibleAt}
          images={images}
          onClose={() => setGalleryVisibleAt(null)}
        />
      )}
      <Center>
        <Flex flexWrap="wrap">
          <Box flexGrow={1} flexShrink={1} flexBasis={0} pr={[0, 3]}>
            <DevelopmentHeader development={development} />
            <Box mb={4}>
              <DevelopmentProjectConsortium development={development} />
            </Box>
            <Box mb={4}>
              <DevelopmentDescription development={development} />
            </Box>
            <Box mb={4}>
              <DevelopmentLocation development={development} />
            </Box>
          </Box>
          <Box>
            <Box
              width={['280px', '300px', '360px']}
              mt={'-110px'}
              pl={[3, 3, 4]}
              css={media({
                display: ['none', 'block'],
                position: ['unset', 'sticky', 'sticky'],
                top: '20px',
                zIndex: 2,
              })}
            >
              <DevelopmentActionCard development={development} />
            </Box>
          </Box>
        </Flex>
        <Box mb={4}>
          <DevelopmentLots development={development} />
        </Box>

        <Box
          width={1}
          css={media({
            display: ['unset', 'none'],
          })}
        >
          <DevelopmentActionBar development={development} />
        </Box>
      </Center>
    </>
  );
};
